import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Dialog, TextField } from "@mui/material";
import StoreMallDirectoryIcon from "@mui/icons-material/StoreMallDirectory";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { selectUser } from "../../../features/userSlice";
import { PageHeader, PageShell } from "../../../components/UI/Page";
import { EmptyState, LoadingState } from "../../../components/UI/States";
import { BackLink, Notice, Pill, SearchField } from "../../../components/Fleet/FleetParts";
import { ErrorLine } from "../../../components/Fleet/ForkliftDialogs";
import { addLocation, fetchLocations } from "../fleetApi";

// Every branch location as one searchable table row; clicking a row opens
// that location's page (LocationDetail.js) to edit its contacts, shifts and
// forklifts or remove it. The search lives in the URL (?q=).

// Everything a search can match. Phone numbers also match by digits, so
// "574876" finds "(574) 876-5465".
const searchText = (l) =>
  [
    l.location,
    l.managerName,
    l.managerEmail,
    l.managerPhone,
    l.generalName,
    l.generalEmail,
    l.generalPhone,
    ...l.shifts.flatMap((s) => [s.name, s.managerName, s.managerEmail, s.managerPhone]),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const phoneDigits = (l) =>
  [l.managerPhone, l.generalPhone, ...l.shifts.map((s) => s.managerPhone)]
    .filter(Boolean)
    .map((phone) => phone.replace(/\D/g, ""));

const Person = ({ name, phone }) =>
  name ? (
    <span className="flex min-w-0 flex-col">
      <span className="truncate">{name}</span>
      {phone && <span className="app-muted text-xs tabular-nums">{phone}</span>}
    </span>
  ) : (
    <span className="app-muted text-sm italic">Not set</span>
  );

const AddLocationDialog = ({ open, onClose, onAdded, userId }) => {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setName("");
      setError(null);
    }
  }, [open]);

  // Same rule as the server: the name goes into kiosk and QR code links.
  const badChar = /[/\\?#%]/.test(name);

  const save = async () => {
    if (!name.trim() || badChar || saving) return;
    setSaving(true);
    setError(null);
    try {
      const { id } = await addLocation(userId, name.trim());
      onAdded(id);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { m: 2 } }} aria-labelledby="add-location-title">
      <div className="p-5 sm:p-6">
        <h2 id="add-location-title" className="text-lg font-semibold">
          Add location
        </h2>
        <p className="app-muted mt-1 text-sm">
          Use the name the branch goes by — it's part of the kiosk link and can't be changed later. You'll add
          contacts, shifts and forklifts on the next page.
        </p>
        <TextField
          fullWidth
          size="small"
          label="Location name"
          value={name}
          autoFocus
          inputProps={{ maxLength: 60 }}
          error={badChar}
          helperText={badChar ? "Can't contain / \\ ? # or % — use & or - instead." : undefined}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && save()}
          sx={{ mt: 2.5 }}
        />
        <ErrorLine>{error}</ErrorLine>
        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button onClick={onClose} color="inherit" disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={save} disabled={!name.trim() || badChar || saving}>
            {saving ? "Adding…" : "Add location"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

const LocationManager = () => {
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [locations, setLocations] = useState(null);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);

  const query = params.get("q") || "";
  const setQuery = (value) => {
    const next = new URLSearchParams(params);
    if (value) next.set("q", value);
    else next.delete("q");
    setParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    if (!user?.id) return;
    setError(null);
    try {
      const data = await fetchLocations(user.id);
      setLocations(data.locations);
    } catch (e) {
      setError(e.message);
    }
  }, [user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return locations || [];
    const digits = q.replace(/\D/g, "");
    return (locations || []).filter(
      (l) =>
        searchText(l).includes(q) ||
        (digits.length >= 3 && phoneDigits(l).some((phone) => phone.includes(digits)))
    );
  }, [locations, query]);

  const open = (id) => navigate(`/fleet/forks/locationmanager/${id}`);

  return (
    <PageShell size="xl">
      <AddLocationDialog
        open={adding}
        onClose={() => setAdding(false)}
        onAdded={(id) => {
          setAdding(false);
          open(id);
        }}
        userId={user?.id}
      />

      <BackLink to="/fleet">Fleet</BackLink>
      <PageHeader
        icon={StoreMallDirectoryIcon}
        tone="teal"
        eyebrow="Fleet"
        title="Location Manager"
        description="Branch locations, their contacts, and their forklifts. Click a location to edit it."
        actions={
          <button type="button" className="app-btn app-btn-primary" onClick={() => setAdding(true)}>
            <AddRoundedIcon sx={{ fontSize: 18 }} /> Add location
          </button>
        }
      />

      {error && <Notice tone="rose">{error}</Notice>}

      {!locations && !error && <LoadingState label="Loading locations…" />}

      {locations && (
        <>
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
            <SearchField
              value={query}
              onChange={setQuery}
              placeholder="Search location, manager, email, or phone…"
              className="w-full sm:w-96"
            />
            <span className="app-muted text-sm sm:ml-auto tabular-nums">
              {visible.length === locations.length
                ? `${locations.length} location${locations.length === 1 ? "" : "s"}`
                : `${visible.length} of ${locations.length} locations`}
            </span>
          </div>

          <div className="app-card overflow-hidden rounded-2xl border">
            {visible.length === 0 ? (
              <EmptyState
                compact
                icon={StoreMallDirectoryIcon}
                title={locations.length ? `No locations match “${query}”.` : "No locations yet."}
                description={locations.length ? undefined : "Add one to get started."}
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="app-table">
                  <thead>
                    <tr>
                      <th className="!px-4">Location</th>
                      <th>Forklifts</th>
                      <th className="hidden md:table-cell">Operations manager</th>
                      <th className="hidden md:table-cell">General manager</th>
                      <th aria-label="Open" />
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((l) => (
                      <tr
                        key={l.id}
                        tabIndex={0}
                        role="link"
                        aria-label={`Open ${l.location}`}
                        onClick={() => open(l.id)}
                        onKeyDown={(e) => e.key === "Enter" && open(l.id)}
                        className="cursor-pointer focus:outline-none focus-visible:bg-[color:var(--app-hover)]"
                      >
                        <td className="!px-4">
                          <span className="flex flex-wrap items-center gap-2">
                            <Link
                              to={`/fleet/forks/locationmanager/${l.id}`}
                              onClick={(e) => e.stopPropagation()}
                              className="font-semibold hover:text-[color:var(--app-accent)]"
                            >
                              {l.location}
                            </Link>
                            {l.shifts.length > 0 && <Pill tone="violet">{l.shifts.length} shifts</Pill>}
                          </span>
                        </td>
                        <td className="whitespace-nowrap tabular-nums">
                          {l.forklifts.total || <span className="app-muted">0</span>}
                          {l.forklifts.outOfService > 0 && (
                            <Pill tone="rose" className="ml-2">
                              {l.forklifts.outOfService} out
                            </Pill>
                          )}
                        </td>
                        <td className="hidden max-w-[16rem] md:table-cell">
                          <Person name={l.managerName} phone={l.managerPhone} />
                        </td>
                        <td className="hidden max-w-[16rem] md:table-cell">
                          <Person name={l.generalName} phone={l.generalPhone} />
                        </td>
                        <td className="w-8 text-right">
                          <ChevronRightRoundedIcon className="app-muted" fontSize="small" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </PageShell>
  );
};

export default LocationManager;
